import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled, { keyframes } from 'styled-components';
import { productService } from '../services/productService';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button/Button';
import Input from '../components/Input/Input';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';

// Animation keyframes
const slideUp = keyframes`
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100px);
    opacity: 0;
  }
`;

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -30px, 0);
  }
  70% {
    transform: translate3d(0, -15px, 0);
  }
  90% {
    transform: translate3d(0, -4px, 0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const ProductDetailWrapper = styled.div`
  min-height: 100vh;
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.theme.colors.background.secondary};
  padding-top: 90px; /* Account for fixed header */
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

// Animated View Cart Notification
const ViewCartNotification = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 300px;
  animation: ${props => props.show ? slideUp : slideDown} 0.3s ease-in-out;
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(100px)'};
  opacity: ${props => props.show ? '1' : '0'};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    left: 20px;
    right: 20px;
    min-width: auto;
  }
`;

const NotificationIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  animation: ${bounce} 1s ease;
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const NotificationMessage = styled.div`
  font-size: 0.8rem;
  opacity: 0.9;
`;

const ViewCartButton = styled(Link)`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
`;

const CloseNotificationButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 0.25rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

// Enhanced Add to Cart Button
const AddToCartButton = styled(Button)`
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(37, 99, 235, 0.3);
  }

  &.adding {
    animation: ${pulse} 0.6s ease;
  }

  &::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    color: white;
    font-size: 1.2rem;
    font-weight: bold;
    transition: transform 0.3s ease;
  }

  &.added::after {
    transform: translate(-50%, -50%) scale(1);
  }

  &.added {
    background-color: #10b981 !important;
    border-color: #10b981 !important;
  }

  .button-text {
    transition: opacity 0.3s ease;
  }

  &.added .button-text {
    opacity: 0;
  }
`;

const Breadcrumb = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  background-color: ${props => props.theme.colors.background.primary};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    transition: color ${props => props.theme.transitions.fast};

    &:hover {
      color: ${props => props.theme.colors.primaryHover};
      text-decoration: underline;
    }
  }
`;

const ProductContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xxxl};
  background-color: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xxl};
  box-shadow: ${props => props.theme.shadows.md};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xxl};
  }
`;

const ProductImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const MainImage = styled.div`
  width: 100%;
  height: 500px;
  background-color: ${props => props.theme.colors.background.secondary};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  border-radius: ${props => props.theme.borderRadius.lg};
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.sm};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => !props.image ? 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)' : 'none'};
    background-size: ${props => !props.image ? '20px 20px' : 'auto'};
    background-position: ${props => !props.image ? '0 0, 0 10px, 10px -10px, -10px 0px' : 'auto'};
    border-radius: ${props => props.theme.borderRadius.lg};
  }
`;

const ProductBadge = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing.lg};
  left: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.semibold};
  z-index: 1;
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: ${props => props.theme.spacing.md};
`;

const Thumbnail = styled.div`
  width: 100%;
  height: 100px;
  background-color: ${props => props.theme.colors.background.secondary};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  border: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  transition: all ${props => props.theme.transitions.fast};
  overflow: hidden;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: scale(1.05);
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const ProductHeader = styled.div`
  padding-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`;

const ProductCategory = styled.span`
  display: inline-block;
  background-color: ${props => props.theme.colors.primaryLight};
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ProductTitle = styled.h1`
  font-size: ${props => props.theme.fontSize.xxxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  line-height: 1.2;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ProductRating = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Stars = styled.div`
  display: flex;
  gap: 4px;
`;

const Star = styled.span`
  color: ${props => props.filled ? '#ffd700' : '#e0e0e0'};
  font-size: ${props => props.theme.fontSize.lg};
  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
`;

const RatingInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const RatingScore = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
`;

const RatingCount = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CurrentPrice = styled.span`
  font-size: ${props => props.theme.fontSize.xxxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
`;

const OriginalPrice = styled.span`
  font-size: ${props => props.theme.fontSize.xl};
  color: ${props => props.theme.colors.text.secondary};
  text-decoration: line-through;
`;

const SavingsAmount = styled.span`
  background-color: ${props => props.theme.colors.success}15;
  color: ${props => props.theme.colors.success};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.semibold};
`;

const ProductMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const MetaLabel = styled.span`
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.fontWeight.medium};
  font-size: ${props => props.theme.fontSize.sm};
`;

const MetaValue = styled.span`
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.fontWeight.semibold};
  font-size: ${props => props.theme.fontSize.base};
`;

const StockStatus = styled.span`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.semibold};
  background-color: ${props => props.inStock ? props.theme.colors.success + '15' : props.theme.colors.error + '15'};
  color: ${props => props.inStock ? props.theme.colors.success : props.theme.colors.error};
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  &::before {
    content: '●';
    font-size: 8px;
  }
`;

const PurchaseSection = styled.div`
  background-color: ${props => props.theme.colors.background.secondary};
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 2px solid ${props => props.theme.colors.border.light};
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const QuantityLabel = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background-color: ${props => props.theme.colors.background.primary};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.lg};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.text.inverse};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 60px;
  height: 40px;
  border: none;
  border-left: 1px solid ${props => props.theme.colors.border.light};
  border-right: 1px solid ${props => props.theme.colors.border.light};
  font-size: ${props => props.theme.fontSize.base};
  text-align: center;
  background-color: ${props => props.theme.colors.background.primary};

  &:focus {
    outline: none;
    background-color: ${props => props.theme.colors.primaryLight};
  }
`;

const AddToCartSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const SingleButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ProductTabs = styled.div`
  background-color: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  overflow: hidden;
`;

const TabHeaders = styled.div`
  display: flex;
  background-color: ${props => props.theme.colors.background.secondary};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`;

const TabHeader = styled.button`
  flex: 1;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border: none;
  background-color: ${props => props.active ? props.theme.colors.background.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  position: relative;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  ${props => props.active && `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background-color: ${props.theme.colors.primary};
    }
  `}
`;

const TabContent = styled.div`
  padding: ${props => props.theme.spacing.xxl};
`;

const ProductDescription = styled.div`
  line-height: 1.7;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.base};

  h1, h2, h3, h4, h5, h6 {
    margin: ${props => props.theme.spacing.xl} 0 ${props => props.theme.spacing.lg} 0;
    color: ${props => props.theme.colors.text.primary};
    font-weight: ${props => props.theme.fontWeight.semibold};
  }

  p {
    margin-bottom: ${props => props.theme.spacing.lg};
  }

  ul, ol {
    margin-bottom: ${props => props.theme.spacing.lg};
    padding-left: ${props => props.theme.spacing.xl};
  }

  li {
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: ${props => props.theme.borderRadius.md};
    margin: ${props => props.theme.spacing.lg} 0;
  }
`;

// Review Section Styled Components
const ReviewsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const ReviewCapabilitiesInfo = styled.div`
  background-color: ${props => props.theme.colors.primaryLight}20;
  border: 1px solid ${props => props.theme.colors.primary}40;
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ReviewsSummary = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.xl};
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.lg};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const OverallRating = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.lg};
`;

const OverallScore = styled.div`
  font-size: ${props => props.theme.fontSize.xxxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const OverallStars = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const TotalReviews = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const RatingBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const RatingLabel = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  min-width: 60px;
`;

const RatingBar = styled.div`
  flex: 1;
  height: 8px;
  background-color: ${props => props.theme.colors.border.light};
  border-radius: 4px;
  overflow: hidden;
`;

const RatingFill = styled.div`
  height: 100%;
  background-color: ${props => props.theme.colors.primary};
  width: ${props => props.percentage}%;
  transition: width ${props => props.theme.transitions.normal};
`;

const RatingCountNum = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  min-width: 30px;
  text-align: right;
`;

const WriteReviewButton = styled(Button)`
  align-self: flex-start;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ReviewForm = styled.form`
  background-color: ${props => props.theme.colors.background.secondary};
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  border: 2px solid ${props => props.theme.colors.primary}20;
`;

const ReviewFormTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FormField = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FormLabel = styled.label`
  display: block;
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const RatingSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const RatingStars = styled.div`
  display: flex;
  gap: 4px;
`;

const RatingStar = styled.button`
  border: none;
  background: none;
  font-size: ${props => props.theme.fontSize.xl};
  color: ${props => props.filled ? '#ffd700' : '#e0e0e0'};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  padding: 4px;

  &:hover {
    color: #ffd700;
    transform: scale(1.1);
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const RatingText = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-left: ${props => props.theme.spacing.sm};
`;

const ReviewInput = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.base};
  font-family: inherit;
  resize: vertical;
  background-color: ${props => props.theme.colors.background.primary};
  color: ${props => props.theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}20;
  }

  &::placeholder {
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const ReviewFormActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.lg};
`;

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const ReviewCard = styled.div`
  background-color: ${props => props.theme.colors.background.secondary};
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  border-left: 4px solid ${props => props.theme.colors.primary};
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.md};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const ReviewerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const ReviewerName = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
`;

const ReviewDate = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const ReviewRating = styled.div`
  display: flex;
  gap: 2px;
`;

const ReviewText = styled.p`
  font-size: ${props => props.theme.fontSize.base};
  line-height: 1.6;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ReviewActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  align-items: center;
`;

const HelpfulButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const ErrorAlert = styled.div`
  background-color: ${props => props.theme.colors.error}08;
  border: 1px solid ${props => props.theme.colors.error}20;
  color: ${props => props.theme.colors.error};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSize.sm};
`;

const SuccessAlert = styled.div`
  background-color: ${props => props.theme.colors.success}08;
  border: 1px solid ${props => props.theme.colors.success}20;
  color: ${props => props.theme.colors.success};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSize.sm};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxxl};
  color: ${props => props.theme.colors.text.secondary};
  
  h3 {
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewCapabilities, setReviewCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [reviewSuccess, setReviewSuccess] = useState(false);
  
  // Cart notification states
  const [showNotification, setShowNotification] = useState(false);
  const [addedProduct, setAddedProduct] = useState(null);
  const [addingState, setAddingState] = useState(null);
  
  const { addItem } = useCart();
  const { isAuthenticated, user } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      review: '',
      reviewer: user?.displayName || user?.firstName || '',
      reviewer_email: user?.email || '',
    }
  });

  // Watch form values for validation
  const watchedReview = watch('review');

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch product data
        const productData = await productService.getProductById(id);
        setProduct(productData);
        
        // Check review capabilities
        const capabilities = await productService.getReviewCapabilities();
        setReviewCapabilities(capabilities);
        
        // Fetch reviews with better error handling
        if (capabilities.canRead) {
          try {
            const reviewsData = await productService.getProductReviews(id);
            setReviews(reviewsData || []);
          } catch (reviewsError) {
            console.warn('Failed to load reviews:', reviewsError.message);
            setReviews([]);
          }
        } else {
          setReviews([]);
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProductData();
    }
  }, [id]);

  // Update form default values when user data is available
  useEffect(() => {
    if (user && isAuthenticated) {
      reset({
        review: '',
        reviewer: user?.displayName || user?.firstName || '',
        reviewer_email: user?.email || '',
      });
    }
  }, [user, isAuthenticated, reset]);

  // Enhanced Add to Cart function with animation
  const handleAddToCart = () => {
    if (product) {
      // Set adding state
      setAddingState('adding');
      
      // Add to cart
      addItem(product, quantity);
      
      // Show added state
      setTimeout(() => {
        setAddingState('added');
        setAddedProduct(product);
        setShowNotification(true);
        
        // Reset button state after animation
        setTimeout(() => {
          setAddingState(null);
        }, 1000);
      }, 300);
    }
  };

  const closeNotification = () => {
    setShowNotification(false);
  };

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const handleQuantityChange = (newQuantity) => {
    setQuantity(Math.max(1, newQuantity));
  };

  const onSubmitReview = async (data) => {
    if (!isAuthenticated) {
      setReviewError('Please log in to submit a review');
      return;
    }

    if (selectedRating === 0) {
      setReviewError('Please select a rating');
      return;
    }

    // Check if reviews can be written
    if (!reviewCapabilities?.canWrite) {
      setReviewError('Review submission is not available on this store');
      return;
    }

    setReviewSubmitting(true);
    setReviewError(null);
    setReviewSuccess(false);

    try {
      const reviewData = {
        product_id: parseInt(id),
        review: data.review.trim(),
        reviewer: data.reviewer.trim() || user?.displayName || user?.firstName || 'Anonymous',
        reviewer_email: data.reviewer_email.trim() || user?.email,
        rating: selectedRating,
      };

      // Validate review data using productService
      const validationErrors = productService.validateReviewData(reviewData);
      if (validationErrors.length > 0) {
        setReviewError(validationErrors[0]);
        return;
      }

      await productService.createProductReview(reviewData);
      
      // Show success message
      setReviewSuccess(true);
      
      // Reset form and state
      reset({
        review: '',
        reviewer: user?.displayName || user?.firstName || '',
        reviewer_email: user?.email || '',
      });
      setSelectedRating(0);
      setShowReviewForm(false);
      
      // Refresh reviews after a delay to allow backend processing
      setTimeout(async () => {
        if (reviewCapabilities?.canRead) {
          try {
            const updatedReviews = await productService.getProductReviews(id);
            setReviews(updatedReviews || []);
          } catch (refreshError) {
            console.warn('Failed to refresh reviews:', refreshError.message);
          }
        }
      }, 1000);
      
    } catch (err) {
      setReviewError(err.message || 'Failed to submit review. Please try again.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleCancelReview = () => {
    setShowReviewForm(false);
    setReviewError(null);
    setReviewSuccess(false);
    setSelectedRating(0);
    reset({
      review: '',
      reviewer: user?.displayName || user?.firstName || '',
      reviewer_email: user?.email || '',
    });
  };

  const renderStars = (rating, size = 'base') => {
    return (
      <Stars>
        {[1, 2, 3, 4, 5].map(star => (
          <Star key={star} filled={star <= rating} size={size}>★</Star>
        ))}
      </Stars>
    );
  };

  const calculateRatingBreakdown = () => {
    if (!reviews.length) return [];
    
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      const rating = Math.round(review.rating);
      if (rating >= 1 && rating <= 5) {
        breakdown[rating] = (breakdown[rating] || 0) + 1;
      }
    });

    return Object.entries(breakdown).map(([rating, count]) => ({
      rating: parseInt(rating),
      count,
      percentage: (count / reviews.length) * 100
    })).reverse();
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return labels[rating] || '';
  };

  if (loading) {
    return <LoadingSpinner fullHeight />;
  }

  if (error || !product) {
    return (
      <ProductDetailWrapper>
        <Container>
          <div className="error">
            Product not found. <Link to="/shop">Back to Shop</Link>
          </div>
        </Container>
      </ProductDetailWrapper>
    );
  }

  const isInStock = productService.isProductInStock(product);
  const productImages = product.images || [];
  const mainImage = productImages[selectedImage]?.src || productService.getProductImage(product);
  
  // Calculate average rating from reviews
  const averageRating = reviews.length > 0 
    ? productService.calculateAverageRating(reviews)
    : (parseFloat(product.average_rating) || 0);
  
  const ratingBreakdown = calculateRatingBreakdown();

  return (
    <ProductDetailWrapper>
      <Container>
        <Breadcrumb>
          <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> / {product.name}
        </Breadcrumb>

        <ProductContent>
          <ProductImageSection>
            <MainImage image={mainImage}>
              {product.on_sale && (
                <ProductBadge>Sale</ProductBadge>
              )}
            </MainImage>
            {productImages.length > 1 && (
              <ThumbnailGrid>
                {productImages.map((image, index) => (
                  <Thumbnail
                    key={index}
                    image={image.src}
                    active={index === selectedImage}
                    onClick={() => setSelectedImage(index)}
                  />
                ))}
              </ThumbnailGrid>
            )}
          </ProductImageSection>

          <ProductInfo>
            <ProductHeader>
              {product.categories && product.categories.length > 0 && (
                <ProductCategory>
                  {product.categories[0].name}
                </ProductCategory>
              )}
              <ProductTitle>{product.name}</ProductTitle>
              
              <ProductRating>
                {renderStars(averageRating)}
                <RatingInfo>
                  <RatingScore>{averageRating.toFixed(1)} out of 5</RatingScore>
                  <RatingCount>({reviews.length} reviews)</RatingCount>
                </RatingInfo>
              </ProductRating>
            </ProductHeader>

            <ProductPrice>
              <CurrentPrice>
                {productService.formatPrice(product.price)}
              </CurrentPrice>
              {product.regular_price !== product.price && (
                <>
                  <OriginalPrice>
                    {productService.formatPrice(product.regular_price)}
                  </OriginalPrice>
                  <SavingsAmount>
                    Save {productService.formatPrice(product.regular_price - product.price)}
                  </SavingsAmount>
                </>
              )}
            </ProductPrice>

            <ProductMeta>
              <MetaItem>
                <MetaLabel>Availability</MetaLabel>
                <MetaValue>
                  <StockStatus inStock={isInStock}>
                    {isInStock ? 'In Stock' : 'Out of Stock'}
                  </StockStatus>
                </MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>SKU</MetaLabel>
                <MetaValue>{product.sku || 'N/A'}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Type</MetaLabel>
                <MetaValue>{product.type || 'Digital Product'}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Downloads</MetaLabel>
                <MetaValue>{product.download_limit || 'Unlimited'}</MetaValue>
              </MetaItem>
            </ProductMeta>

            {isInStock && (
              <PurchaseSection>
                <QuantitySelector>
                  <QuantityLabel>Quantity:</QuantityLabel>
                  <QuantityControls>
                    <QuantityButton
                      type="button"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                    >
                      −
                    </QuantityButton>
                    <QuantityInput
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                    />
                    <QuantityButton
                      type="button"
                      onClick={() => handleQuantityChange(quantity + 1)}
                    >
                      +
                    </QuantityButton>
                  </QuantityControls>
                </QuantitySelector>

                <AddToCartSection>
                  <SingleButtonContainer>
                    <AddToCartButton 
                      onClick={handleAddToCart} 
                      size="lg" 
                      className={addingState || ''}
                      style={{ width: '100%' }}
                    >
                      <span className="button-text">
                        {addingState === 'adding' ? 'Adding to Cart...' :
                         addingState === 'added' ? 'Added!' : 
                         `Add to Cart - ${productService.formatPrice(product.price * quantity)}`}
                      </span>
                    </AddToCartButton>
                  </SingleButtonContainer>
                </AddToCartSection>
              </PurchaseSection>
            )}
          </ProductInfo>
        </ProductContent>

        <ProductTabs>
          <TabHeaders>
            <TabHeader 
              active={activeTab === 'description'} 
              onClick={() => setActiveTab('description')}
            >
              Description
            </TabHeader>
            <TabHeader 
              active={activeTab === 'reviews'} 
              onClick={() => setActiveTab('reviews')}
            >
              Reviews ({reviews.length})
            </TabHeader>
          </TabHeaders>

          <TabContent>
            {activeTab === 'description' && product.description && (
              <ProductDescription>
                <div dangerouslySetInnerHTML={{ __html: product.description }} />
              </ProductDescription>
            )}

            {activeTab === 'reviews' && (
              <ReviewsSection>
                {reviewSuccess && (
                  <SuccessAlert>
                    Review submitted successfully! 
                    {reviewCapabilities?.method === 'mock' 
                      ? ' (This is a demo submission in development mode)'
                      : ' It may take some time to appear after moderation.'
                    }
                  </SuccessAlert>
                )}

                <ReviewsSummary>
                  <OverallRating>
                    <OverallScore>{averageRating.toFixed(1)}</OverallScore>
                    <OverallStars>
                      {renderStars(averageRating)}
                    </OverallStars>
                    <TotalReviews>Based on {reviews.length} reviews</TotalReviews>
                  </OverallRating>

                  <RatingBreakdown>
                    {ratingBreakdown.map(({ rating, count, percentage }) => (
                      <RatingRow key={rating}>
                        <RatingLabel>{rating} stars</RatingLabel>
                        <RatingBar>
                          <RatingFill percentage={percentage} />
                        </RatingBar>
                        <RatingCountNum>{count}</RatingCountNum>
                      </RatingRow>
                    ))}
                  </RatingBreakdown>
                </ReviewsSummary>

                {/* Write Review Section */}
                {isAuthenticated ? (
                  <>
                    {!showReviewForm && reviewCapabilities?.canWrite && (
                      <WriteReviewButton 
                        variant="outline"
                        onClick={() => {
                          setShowReviewForm(true);
                          setReviewError(null);
                          setReviewSuccess(false);
                        }}
                      >
                        Write a Review
                      </WriteReviewButton>
                    )}

                    {showReviewForm && reviewCapabilities?.canWrite && (
                      <ReviewForm onSubmit={handleSubmit(onSubmitReview)}>
                        <ReviewFormTitle>Write Your Review</ReviewFormTitle>
                        
                        {reviewError && (
                          <ErrorAlert>{reviewError}</ErrorAlert>
                        )}
                        
                        {/* Rating Selector */}
                        <FormField>
                          <FormLabel>Your Rating *</FormLabel>
                          <RatingSelector>
                            <RatingStars>
                              {[1, 2, 3, 4, 5].map(star => (
                                <RatingStar
                                  key={star}
                                  type="button"
                                  filled={star <= (hoverRating || selectedRating)}
                                  onClick={() => setSelectedRating(star)}
                                  onMouseEnter={() => setHoverRating(star)}
                                  onMouseLeave={() => setHoverRating(0)}
                                  aria-label={`Rate ${star} stars`}
                                >
                                  ★
                                </RatingStar>
                              ))}
                            </RatingStars>
                            <RatingText>
                              {selectedRating > 0 && `${selectedRating}/5 - ${getRatingLabel(selectedRating)}`}
                            </RatingText>
                          </RatingSelector>
                        </FormField>

                        {/* Reviewer Name */}
                        <FormField>
                          <Input
                            label="Your Name *"
                            placeholder="Enter your name"
                            error={errors.reviewer?.message}
                            {...register('reviewer', {
                              required: 'Name is required',
                              minLength: {
                                value: 2,
                                message: 'Name must be at least 2 characters long',
                              },
                            })}
                          />
                        </FormField>

                        {/* Reviewer Email */}
                        <FormField>
                          <Input
                            label="Your Email *"
                            type="email"
                            placeholder="Enter your email"
                            error={errors.reviewer_email?.message}
                            {...register('reviewer_email', {
                              required: 'Email is required',
                              pattern: {
                                value: /^\S+@\S+\.\S+$/,
                                message: 'Please enter a valid email address',
                              },
                            })}
                          />
                        </FormField>

                        {/* Review Text */}
                        <FormField>
                          <FormLabel htmlFor="review">Your Review *</FormLabel>
                          <ReviewInput
                            id="review"
                            placeholder="Share your experience with this product..."
                            {...register('review', {
                              required: 'Please write your review',
                              minLength: {
                                value: 10,
                                message: 'Review must be at least 10 characters long',
                              },
                              maxLength: {
                                value: 1000,
                                message: 'Review must be less than 1000 characters',
                              },
                            })}
                          />
                          {errors.review && (
                            <ErrorAlert style={{ marginTop: '0.5rem' }}>
                              {errors.review.message}
                            </ErrorAlert>
                          )}
                          <div style={{ 
                            fontSize: '0.875rem', 
                            color: '#666', 
                            marginTop: '0.5rem',
                            textAlign: 'right'
                          }}>
                            {watchedReview?.length || 0}/1000 characters
                          </div>
                        </FormField>

                        <ReviewFormActions>
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={handleCancelReview}
                            disabled={reviewSubmitting}
                          >
                            Cancel
                          </Button>
                          <Button 
                            type="submit" 
                            disabled={reviewSubmitting || selectedRating === 0}
                          >
                            {reviewSubmitting ? (
                              <>
                                <LoadingSpinner size="16px" showText={false} />
                                <span style={{ marginLeft: '0.5rem' }}>Submitting...</span>
                              </>
                            ) : (
                              'Submit Review'
                            )}
                          </Button>
                        </ReviewFormActions>
                      </ReviewForm>
                    )}

                    {!reviewCapabilities?.canWrite && (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '2rem', 
                        background: '#f8f9fa', 
                        borderRadius: '8px', 
                        marginBottom: '2rem' 
                      }}>
                        <p>Review submission is not available on this store.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem', 
                    background: '#f8f9fa', 
                    borderRadius: '8px', 
                    marginBottom: '2rem' 
                  }}>
                    <p>Please <Link to="/signin">sign in</Link> to write a review.</p>
                  </div>
                )}

                {/* Reviews List */}
                {reviews.length > 0 ? (
                  <ReviewsList>
                    {reviews.map((review) => (
                      <ReviewCard key={review.id}>
                        <ReviewHeader>
                          <ReviewerInfo>
                            <ReviewerName>
                              {review.reviewer}
                              {review.verified && (
                                <span style={{ 
                                  color: '#22c55e', 
                                  fontSize: '0.75rem', 
                                  marginLeft: '0.5rem',
                                  fontWeight: 'normal'
                                }}>
                                  ✓ Verified Purchase
                                </span>
                              )}
                            </ReviewerName>
                            <ReviewDate>
                              {new Date(review.date_created).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </ReviewDate>
                          </ReviewerInfo>
                          <ReviewRating>
                            {renderStars(review.rating)}
                          </ReviewRating>
                        </ReviewHeader>
                        
                        <ReviewText>{review.review}</ReviewText>
                        
                        <ReviewActions>
                          <HelpfulButton>
                            👍 Helpful ({Math.floor(Math.random() * 10)})
                          </HelpfulButton>
                          <HelpfulButton>
                            👎 Not Helpful
                          </HelpfulButton>
                        </ReviewActions>
                      </ReviewCard>
                    ))}
                  </ReviewsList>
                ) : (
                  <EmptyState>
                    <h3>No reviews yet</h3>
                    <p>Be the first to review this product!</p>
                  </EmptyState>
                )}
              </ReviewsSection>
            )}
          </TabContent>
        </ProductTabs>

        {/* Animated View Cart Notification */}
        <ViewCartNotification show={showNotification}>
          <NotificationIcon>🛒</NotificationIcon>
          <NotificationContent>
            <NotificationTitle>Added to Cart!</NotificationTitle>
            <NotificationMessage>
              {addedProduct?.name} has been added to your cart
            </NotificationMessage>
          </NotificationContent>
          <ViewCartButton to="/cart">
            View Cart
          </ViewCartButton>
          <CloseNotificationButton onClick={closeNotification}>
            ×
          </CloseNotificationButton>
        </ViewCartNotification>
      </Container>
    </ProductDetailWrapper>
  );
};

export default ProductDetailPage;